import type { World2DCamera } from '../camera';
import type { DynamicActorRenderItem, DynamicMarkerRenderItem, DynamicSceneRenderData } from '../contracts';
import type { GridGeometry } from '../../../grid';
import { resolveTileDrawMetrics } from '../../tilemap/board';
import { drawCachedActorSprite, getCachedActorSpriteDrawSize } from '../../ActorSpriteCache';
import { drawMobVisual, getMobVisualDrawSize } from '../../mobVisuals/index';
import { drawCachedLabel } from '../../textSpriteCache';

export interface DynamicHitTarget {
  readonly id: string;
  readonly playerId?: string;
  readonly kind: 'self' | 'remotePlayer' | 'mob';
  readonly entityType?: string;
  readonly displayName?: string;
  readonly guildName?: string;
  readonly tradeTargetName?: string;
  readonly x: number;
  readonly y: number;
  readonly centerX: number;
  readonly centerY: number;
  readonly radius: number;
  readonly hp?: number;
  readonly maxHp?: number;
  readonly stamina?: number;
  readonly maxStamina?: number;
  readonly mobInventoryValue?: number | null;
  readonly mobInventoryItemsJson?: string | null;
  readonly rotation: number;
}

export class DynamicEntityLayer {
  private lastHitTargets: DynamicHitTarget[] = [];

  public draw(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    scene: DynamicSceneRenderData,
    grid: GridGeometry,
    overscanPx = 0,
  ): void {
    this.lastHitTargets = [];

    for (const marker of scene.markers) {
      this.drawMarker(ctx, camera, marker, grid, overscanPx);
    }

    for (const actor of scene.actors) {
      this.drawActor(ctx, camera, actor, grid, overscanPx);
    }
  }

  public hitTest(screenX: number, screenY: number): DynamicHitTarget | null {
    let best: DynamicHitTarget | null = null;
    let bestDistanceSq = Number.POSITIVE_INFINITY;
    for (const target of this.lastHitTargets) {
      const dx = screenX - target.centerX;
      const dy = screenY - target.centerY;
      const distanceSq = dx * dx + dy * dy;
      if (distanceSq > target.radius * target.radius || distanceSq >= bestDistanceSq) {
        continue;
      }
      best = target;
      bestDistanceSq = distanceSq;
    }
    return best;
  }

  private drawActor(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    actor: DynamicActorRenderItem,
    grid: GridGeometry,
    overscanPx: number,
  ): void {
    const size = camera.getPixelsPerWorldUnit();
    const tileMetrics = resolveTileDrawMetrics(grid, size, actor.x, actor.y);
    const centerWorldX = actor.worldCenterX ?? tileMetrics.centerX;
    const centerWorldY = actor.worldCenterY ?? tileMetrics.centerY;
    const center = camera.worldPointToScreen({ x: centerWorldX, y: centerWorldY }, grid);
    const radius = Math.max(8, size * 0.28);
    const drawSize = actor.entityType && actor.entityType !== 'player'
      ? getMobVisualDrawSize(actor.entityType, size)
      : getCachedActorSpriteDrawSize(size);
    const labelPadding = actor.displayName ? Math.max(18, size * 0.7) : 8;
    const extent = Math.max(radius * 1.3, drawSize * 0.55) + labelPadding;
    if (!isVisibleInViewport(center.x, center.y, extent, camera, overscanPx)) {
      return;
    }

    ctx.save();
    if (actor.entityType && actor.entityType !== 'player') {
      drawMobVisual(ctx, {
        entityType: actor.entityType,
        centerX: center.x,
        centerY: center.y,
        size,
        rotation: actor.rotation,
        color: actor.color,
        alpha: actor.alpha ?? 1,
        isMoving: actor.isMoving === true,
      });
    } else {
      drawCachedActorSprite(ctx, {
        centerX: center.x,
        centerY: center.y,
        size,
        rotation: actor.rotation,
        color: actor.color,
        accessoriesJson: actor.accessoriesJson,
        alpha: actor.alpha ?? 1,
      });
    }

    if (actor.displayName) {
      drawCachedLabel(ctx, {
        text: actor.displayName,
        centerX: center.x,
        centerY: center.y - size * 0.65,
        fontPx: Math.max(12, size * 0.26),
        alpha: actor.alpha ?? 1,
      });
    }
    ctx.restore();

    this.lastHitTargets.push({
      id: actor.id,
      playerId: actor.playerId,
      kind: actor.kind,
      entityType: actor.entityType,
      displayName: actor.displayName,
      guildName: actor.guildName,
      tradeTargetName: actor.kind === 'remotePlayer'
        ? (actor.displayName?.trim() || actor.id)
        : undefined,
      x: actor.x,
      y: actor.y,
      centerX: center.x,
      centerY: center.y,
      radius: Math.max(radius * 1.3, drawSize * 0.45),
      hp: actor.hp,
      maxHp: actor.maxHp,
      stamina: actor.stamina,
      maxStamina: actor.maxStamina,
      mobInventoryValue: actor.mobInventoryValue ?? null,
      mobInventoryItemsJson: actor.mobInventoryItemsJson ?? null,
      rotation: actor.rotation,
    });
  }

  private drawMarker(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    marker: DynamicMarkerRenderItem,
    grid: GridGeometry,
    overscanPx: number,
  ): void {
    const pixelsPerWorldUnit = camera.getPixelsPerWorldUnit();
    const tileMetrics = resolveTileDrawMetrics(grid, pixelsPerWorldUnit, marker.x, marker.y);
    const center = camera.worldPointToScreen({ x: tileMetrics.centerX, y: tileMetrics.centerY }, grid);
    const size = Math.max(5, pixelsPerWorldUnit * 0.12);
    if (!isVisibleInViewport(center.x, center.y, size + 4, camera, overscanPx)) {
      return;
    }
    ctx.save();
    if (marker.kind === 'chest') {
      ctx.fillStyle = '#f59e0b';
      ctx.strokeStyle = 'rgba(120,53,15,0.9)';
      ctx.lineWidth = 2;
      ctx.fillRect(center.x - size, center.y - size, size * 2, size * 2);
      ctx.strokeRect(center.x - size, center.y - size, size * 2, size * 2);
    } else {
      ctx.fillStyle = '#a855f7';
      ctx.strokeStyle = 'rgba(46,16,101,0.9)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(center.x, center.y - size);
      ctx.lineTo(center.x + size, center.y);
      ctx.lineTo(center.x, center.y + size);
      ctx.lineTo(center.x - size, center.y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }
    ctx.restore();
  }

}

function isVisibleInViewport(
  centerX: number,
  centerY: number,
  radius: number,
  camera: World2DCamera,
  overscanPx: number,
): boolean {
  const viewport = camera.getViewportSize();
  return !(
    centerX + radius < -overscanPx
    || centerY + radius < -overscanPx
    || centerX - radius > viewport.width + overscanPx
    || centerY - radius > viewport.height + overscanPx
  );
}
